import { useFragmentsViewCapture } from '@app/modules/fragments/hooks/useFragmentsViewCapture';
import { DateTimeStr } from '@mi-front/core';
import { Box } from '@mui/material';
import { useState } from 'react';
import { useFragment } from '../../../hooks/useFragment';
import { TranscriptionBlocksList } from '../blocks/TranscriptionBlocksList';
import { useFollowPlayback } from '../hooks/useFollowPlayback';
import { useFragmentTranscription } from '../hooks/useFragmentTranscription';
import { useTranscriptionPlaybackSync } from '../hooks/useTranscriptionPlaybackSync';
import { TranscriptionSearchNoMatches } from '../search/TranscriptionSearchNoMatches';
import { useTranscriptionSearch } from '../search/useTranscriptionSearch';
import { TranscriptionEmpty } from './state/TranscriptionEmpty';
import { TranscriptionToolbar } from './TranscriptionToolbar';

/**
 * Renders loaded transcription content with search and follow-playback behavior.
 */
export function TranscriptionContent() {
  const { fragment } = useFragment();
  const { blocks } = useFragmentTranscription(fragment);
  const fragmentsCapture = useFragmentsViewCapture();

  const [showWordEfficiency, setShowWordEfficiency] = useState(false);
  const { seekToTimeSeconds, isBlockActive, isWordHighlighted } =
    useTranscriptionPlaybackSync(fragment);

  const {
    query: searchQuery,
    setQuery: setSearchQuery,
    normalizedQuery: searchNormalizedQuery,
    matchWordIds,
    displayBlocks,
    noMatches,
  } = useTranscriptionSearch(blocks);

  const {
    followPlayback,
    enableFollowPlayback,
    disableFollowPlayback,
    seekToTimeSecondsAndFollow,
  } = useFollowPlayback({
    searchQuery: searchNormalizedQuery,
    seekToTimeSeconds,
  });

  const hasBlocks = blocks.length > 0;
  const activeBlockId = blocks.find((b) => isBlockActive(b.segment))?.id ?? null;

  const handleSearchQueryChange = (value: string) => {
    fragmentsCapture.transcriptionSearch({ query: value });
    setSearchQuery(value);
  };

  const handleSyncIconClick = () => {
    fragmentsCapture.transcriptionSyncToggle({ enabled: true });
    enableFollowPlayback();
  };

  const handleWordEfficiencyClick = () => {
    const nextEnabled = !showWordEfficiency;
    fragmentsCapture.transcriptionWordEfficiencyToggle({ enabled: nextEnabled });
    setShowWordEfficiency(nextEnabled);
  };

  const handleWordClick = (wordStartSeconds: number, chunkTime: DateTimeStr) => {
    fragmentsCapture.transcriptionWordClick({
      wordStartSeconds,
      chunkTime,
    });
    seekToTimeSecondsAndFollow(wordStartSeconds, chunkTime);
  };

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {hasBlocks ? (
        <>
          <TranscriptionToolbar
            searchQuery={searchQuery}
            onSearchQueryChange={handleSearchQueryChange}
            followPlayback={followPlayback}
            onSyncIconClick={handleSyncIconClick}
            showWordEfficiency={showWordEfficiency}
            onWordEfficiencyClick={handleWordEfficiencyClick}
          />
          {noMatches ? (
            <TranscriptionSearchNoMatches />
          ) : (
            <TranscriptionBlocksList
              blocks={displayBlocks}
              activeBlockId={activeBlockId}
              followPlayback={followPlayback}
              showWordEfficiency={showWordEfficiency}
              matchWordIds={matchWordIds}
              onUserInterruptedScroll={disableFollowPlayback}
              seekToTimeSeconds={seekToTimeSecondsAndFollow}
              onWordClick={handleWordClick}
              isBlockActive={isBlockActive}
              isWordHighlighted={isWordHighlighted}
            />
          )}
        </>
      ) : (
        <TranscriptionEmpty />
      )}
    </Box>
  );
}
